import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { Button, Loader } from 'ncoded-component-library';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import TimelineNode from 'components/TimelineNode';
import api from 'api';
import {
  BUYERS_ACTIVITY_PLATFORM_TYPE_CODE,
  BUYERS_ACTIVITY_TRACK_EVENTS,
  BUYERS_ACTIVITY_TRACK_TYPE,
  BuyersActivityTrack,
  BuyersActivityTrackEvents,
  BuyersActivityTrackType,
  Lead,
  MEMBER_ACTIVITY_PAGE_TO_EXPORT_PAGE,
} from 'models/Lead';
import TransWithNda from 'components/TransWithNda';
import { DATA_ROOM_NODE_TAG } from 'router/subrouters/DataRoom/models/DataRoomNode';
import SoftSignalIcon from 'components/SoftSignalIcon';
import { LeadStagesCopies } from 'models/DealRoom';
import { MAX_LIMIT } from 'data/global';
import Nbio from '../Nbio';
import utils, {
  checkIsUserSuperAdmin,
  getHasElementsInArray,
  getIsArrayEmpty,
  getUserFullName,
  getUserFullNameOrEmail,
} from 'utils';
import uniqueId from 'lodash/uniqueId';
import useLeadNotificationCopy from 'hooks/useLeadNotificationCopy';
import NotificationMediumTag from 'components/NotificationMediumTag';
import { Trans, useTranslation } from 'react-i18next';
import ReportsEmptyState from 'router/subrouters/DataRoom/subrouters/Reports/pages/Reports/components/ReportsEmptyState';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import {
  ErrorViewedProps,
  MemberActivityFilterUpdatedEventProps,
  MemberActivityPage,
} from 'models/MixpanelEvents';
import { getMixpanelErrorProps } from 'axiosUtils';
import DownloadIcon from 'icons/Download.icon';
import useCallbackRef from 'hooks/useCallbackRef';
import DownloadStatusContext from 'providers/DownloadStatus/DownloadStatus.context';
import ExportMemberActivityModal from 'router/subrouters/DataRoom/components/ExportMemberActivityModal';
import EventTypeFilters from './components/EventTypeFilters';
import { getActivityCopyKey, getEventKey } from './data';
import { getActivityNodeName } from './utils';

import './LeadTimeActivity.styles.scss';

type LeadTimeActivityProps = {
  className?: string;
  lead: Partial<Lead>;
  dealId: string;
  dealName: string;
  dataRoomDisabled?: boolean;
  initialSelectedEventTypes: BuyersActivityTrackType[];
  eventTypes?: BuyersActivityTrackType[];
  memberActivityPage: MemberActivityPage;
  leadStagesCopies?: LeadStagesCopies;
};

const LeadTimeActivity: React.FC<LeadTimeActivityProps> = ({
  className,
  lead,
  dealId,
  dealName,
  dataRoomDisabled,
  initialSelectedEventTypes,
  eventTypes,
  memberActivityPage,
  leadStagesCopies,
}) => {
  const { _id: leadId } = lead || {};

  const leadName = getUserFullNameOrEmail(lead);

  const [selectedEventTypes, setSelectedEventTypes] = useState(
    initialSelectedEventTypes,
  );

  const [isLoading, setIsLoading] = useState(true);
  const [activities, setActivities] = useState<Partial<BuyersActivityTrack>[]>(
    [],
  );

  const [exportModal, exportModalRef] = useCallbackRef<ModalRef>();

  const { isLeadAnalyticsExportInProgress } = useContext(DownloadStatusContext);

  const hasSelectedEventType = getHasElementsInArray(selectedEventTypes);

  const activitiesToShow = useMemo(
    () => activities.filter(({ type }) => selectedEventTypes.includes(type)),
    [activities, selectedEventTypes],
  );

  const { t } = useTranslation();

  const getCopy = useLeadNotificationCopy();

  const onNewActivity = useCallback(
    (activity: Partial<BuyersActivityTrack>) =>
      setActivities((prevActivities) => [activity, ...prevActivities]),
    [],
  );

  const getMixpanelProps = useGetMixpanelProps();

  const onEventTypeChange = useCallback(
    (type: BuyersActivityTrackType, checked: boolean) => {
      const next = checked
        ? [...selectedEventTypes, type]
        : selectedEventTypes.filter((old) => old !== type);

      mixpanelService.memberActivityFilterUpdated(
        getMixpanelProps({
          dealId,
          dealName,
          memberId: leadId,
          memberName: leadName,
          memberActivityPage,
          platformIncluded: next.includes(BUYERS_ACTIVITY_TRACK_TYPE.PLATFORM),
          notificationsIncluded: next.includes(
            BUYERS_ACTIVITY_TRACK_TYPE.NOTIFICATION,
          ),
          dataRoomIncluded: next.includes(BUYERS_ACTIVITY_TRACK_TYPE.DATA_ROOM),
        }) as MemberActivityFilterUpdatedEventProps,
      );
      setSelectedEventTypes(next);
    },
    [
      selectedEventTypes,
      dealId,
      dealName,
      leadId,
      leadName,
      memberActivityPage,
      getMixpanelProps,
    ],
  );

  const classes = classNames('lead-time-activity', 'white-panel', className);

  const idForSubmitOffer = useMemo(
    () =>
      activities.find(
        (activity) =>
          getEventKey(activity) === BUYERS_ACTIVITY_TRACK_EVENTS.SUBMIT_OFFER,
      )?._id,
    [activities],
  );

  const addNewActivity = useCallback(
    (eventType: BuyersActivityTrackEvents) =>
      onNewActivity({
        type: BUYERS_ACTIVITY_TRACK_TYPE.PLATFORM,
        platformTypeCode:
          BUYERS_ACTIVITY_PLATFORM_TYPE_CODE[
            eventType as keyof typeof BUYERS_ACTIVITY_PLATFORM_TYPE_CODE
          ],
        _id: uniqueId(),
        createdAt: new Date(),
      }),
    [onNewActivity],
  );

  useEffect(() => {
    (async () => {
      setIsLoading(true);

      try {
        const {
          data: { items },
        } = await api.portfolio.seller.getActivities(leadId, {
          $sort: '-createdAt',
          $limit: MAX_LIMIT,
        });
        setActivities(items);
      } catch (e) {
        mixpanelService.errorViewed(
          getMixpanelProps(getMixpanelErrorProps(e)) as ErrorViewedProps,
        );
      } finally {
        setIsLoading(false);
      }
    })();
  }, [leadId, getMixpanelProps]);

  return (
    <div className={classes}>
      <div className="lead-time-activity__header">
        <EventTypeFilters
          eventTypes={eventTypes}
          selectedEventTypes={selectedEventTypes}
          dataRoomDisabled={dataRoomDisabled}
          onChange={onEventTypeChange}
        />
        <Button
          variant="outline"
          icon={DownloadIcon}
          disabled={isLoading || isLeadAnalyticsExportInProgress}
          onClick={exportModal?.open}
        >
          {t('export')}
        </Button>
      </div>

      <div className="lead-time-activity__content">
        {isLoading ? (
          <Loader />
        ) : !hasSelectedEventType ? (
          <p className="choose-event-group-empty-state">
            {t('chooseEventGroupEmptyState')}
          </p>
        ) : getIsArrayEmpty(activitiesToShow) ? (
          <ReportsEmptyState />
        ) : (
          activitiesToShow.map((activity) => {
            const {
              _id,
              createdAt,
              dataRoomNodeTag,
              oldStage,
              newStage,
              softSignal,
              dataRoomNode,
              progressedByUser,
              envelopeTemplateName,
              question,
              answer,
              withdrawnInterest,
              leadNotificationTypeCode,
              leadNotificationStatus,
              leadNotificationChannel,
              timeSpent,
              releasedDataRoomNode,
              stage,
              releasedFilesCount,
            } = activity;

            const { type: timeSpentType, count: timeSpentCount } =
              utils.timeSpentInSecondsHumanize(timeSpent);

            return (
              <TimelineNode
                key={_id}
                date={createdAt}
                text={
                  <TransWithNda
                    i18nKey={getActivityCopyKey(activity)}
                    envelopeTemplateName={envelopeTemplateName}
                    isParagraph={false}
                    values={{
                      oldStage: leadStagesCopies?.[oldStage] || 'Withdraw',
                      newStage: leadStagesCopies?.[newStage] || 'Withdraw',
                      dataRoomNodeName: getActivityNodeName(dataRoomNode),
                      progressedByFullName: checkIsUserSuperAdmin(
                        progressedByUser?.role,
                      )
                        ? 'Resolve team'
                        : getUserFullName(
                            progressedByUser?.firstName,
                            progressedByUser?.lastName,
                          ),
                      tag: DATA_ROOM_NODE_TAG[dataRoomNodeTag],
                      timeSpent: t(
                        `timeSpentInSecondsHumanizeLong.${timeSpentType}`,
                        { count: timeSpentCount },
                      ),
                      leadName,
                      dealName,
                      notificationCopy: getCopy(
                        leadNotificationTypeCode,
                        leadNotificationStatus,
                        lead,
                      ),
                      withdrawnText: t(
                        withdrawnInterest
                          ? 'theyWithdrawnInterest'
                          : 'theyDidntWithdrawnInterest',
                      ),
                      releasedDataRoomNodeName:
                        getActivityNodeName(releasedDataRoomNode),
                      stage: leadStagesCopies?.[stage],
                      releasedFilesCount,
                    }}
                    components={{
                      softSignalIcon: (
                        <SoftSignalIcon softSignal={softSignal} />
                      ),
                      medium: (
                        <NotificationMediumTag type={leadNotificationChannel} />
                      ),
                      leadQuestionLine: question ? (
                        <Trans
                          i18nKey="leadQuestionLine"
                          values={{ question }}
                          components={{ lineBreak: <br />, bold: <strong /> }}
                        />
                      ) : (
                        <></>
                      ),
                      leadAnswerLine: answer ? (
                        <Trans
                          i18nKey="leadAnswerLine"
                          values={{ answer }}
                          components={{ lineBreak: <br />, bold: <strong /> }}
                        />
                      ) : (
                        <></>
                      ),
                    }}
                  />
                }
              >
                {_id === idForSubmitOffer && (
                  <Nbio addNewActivity={addNewActivity} />
                )}
              </TimelineNode>
            );
          })
        )}
      </div>

      <ExportMemberActivityModal
        ref={exportModalRef}
        leadId={leadId}
        dataRoomDisabled={dataRoomDisabled}
        memberActivityPage={
          MEMBER_ACTIVITY_PAGE_TO_EXPORT_PAGE[memberActivityPage]
        }
      />
    </div>
  );
};

export default LeadTimeActivity;
